import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtDateShort } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Page";

const NEW = { date: new Date().toISOString().slice(0, 10), all_day: true, start: "09:00", end: "17:00", note: "" };

export default function Verfuegbarkeit() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NEW);

  const load = () => api.get("/availability").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.date) return toast.error("Bitte Datum wählen");
    try {
      await api.post("/availability", form);
      toast.success("Eintrag gespeichert");
      setOpen(false);
      setForm(NEW);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/availability/${id}`); toast.success("Eintrag gelöscht"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = rows.filter((r) => r.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = rows.filter((r) => r.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const Item = ({ r }) => (
    <Card className="p-4 border-slate-200 rounded-xl flex items-center justify-between gap-3" data-testid={`avail-row-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center"><CalendarOff className="h-5 w-5" /></div>
        <div>
          <div className="font-medium text-slate-900">{fmtDateShort(r.date)}</div>
          <div className="text-sm text-slate-500">
            {r.all_day ? "Ganztägig nicht verfügbar" : `Nicht verfügbar ${r.start || "?"}–${r.end || "?"}`}
            {r.note ? ` · ${r.note}` : ""}
          </div>
        </div>
      </div>
      <button onClick={() => del(r.id)} data-testid={`avail-delete-${r.id}`} className="text-slate-300 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
    </Card>
  );

  return (
    <div>
      <PageHeader
        title="Meine Verfügbarkeit"
        subtitle="Trage ein, wann du NICHT arbeiten kannst. Dein Admin sieht das bei der Schichtplanung."
        testid="verfuegbarkeit-header"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(NEW); }}>
            <DialogTrigger asChild><Button data-testid="avail-open" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-2" /> Nicht verfügbar</Button></DialogTrigger>
            <DialogContent data-testid="avail-dialog">
              <DialogHeader><DialogTitle>Nicht verfügbar eintragen</DialogTitle><DialogDescription>Wähle einen Tag, an dem du nicht arbeiten kannst.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Datum</Label><Input type="date" data-testid="avail-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <label className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm font-medium text-slate-700">Ganztägig</span>
                  <Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} data-testid="avail-allday" />
                </label>
                {!form.all_day && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Von</Label><Input type="time" data-testid="avail-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Bis</Label><Input type="time" data-testid="avail-end" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
                  </div>
                )}
                <div className="space-y-2"><Label>Notiz (optional)</Label><Input data-testid="avail-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="z. B. Arzttermin" /></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="avail-submit" className="brand-bg text-white hover:opacity-90">Speichern</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="max-w-2xl grid gap-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Anstehend</div>
          <div className="grid gap-3">
            {upcoming.length === 0 ? (
              <Card className="border-slate-200 rounded-xl"><EmptyState icon={CalendarOff} title="Keine Einträge" testid="avail-empty" /></Card>
            ) : upcoming.map((r) => <Item key={r.id} r={r} />)}
          </div>
        </div>
        {past.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Vergangen</div>
            <div className="grid gap-3 opacity-60">{past.map((r) => <Item key={r.id} r={r} />)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
